<template lang="pug">
.container(:class="{ 'custom-controls': showDimensions }")
  .rackbuild(:class="{loading: loading}" v-element-visibility="setVisible")
    .patent-image
      ImageFlag(v-show="!loading" :imageFlagMedia='imageFlagMedia')
    Icon.threed-icon(v-if="!areMeasurementsVisible" name="rackbuilder/360" fill="black" v-show="!loading")
    .loading(v-show="loading")
      spinner
    .rackbuilder-pad
    .threecontainer(ref="three" key="threejscontainer")
    rogue-engine-controls.controls(:class="{ 'hide-on-mobile': showDimensions }" v-if="controls && !loading && engine" :engine="engine" :areMeasurementsVisible="areMeasurementsVisible")
    CameraDebug(v-if="isCameraDebug && !loading && engine.engine" :engine="engine" :fitOffset="cameraFitOffset")
  ViewDimensionsControls(
    v-if="showDimensions && !loading"
    :areMeasurementsVisible="areMeasurementsVisible"
    :focusedVirtualMeshId="focusedVirtualMeshId"
    :controls="controls"
    :engine="engine"
    :showEquipped="supportsEquippedView ? showEquipped : undefined"
    @toggleMeasurements="toggleMeasurements"
    @exitOverviewMode="exitOverviewMode"
    @resetCamera="resetCamera"
    @update:showEquipped="showEquipped = $event"
  )
</template>
<script lang="ts" setup>
import { ref, watch, computed, toRef, defineAsyncComponent } from 'vue'
import { type RogueEngineOptions, type PartDefinition } from '@rogue/rackbuilder'
import { vElementVisibility } from '@vueuse/components'
import { useRoute } from 'vue-router'
import { useRackbuilder } from '../composables/RackbuilderComposable'
import { use3DEngine } from '../composables/RogueEngineComposable'
import { MEASUREMENT_CONFIG, DEFAULT_MEASUREMENT_CONFIG } from '../utils/measurement-config'
import rogueEngineControls from './ui/rogue-engine-controls.vue'
import { useProductStore } from '@/js/stores/productStore'
import type { BuyRequest } from '@/js/services/types/order'
import { isEnabledCustomProduct } from '@/js/customProducts/registry'
import ViewDimensionsControls from './ui/view-dimensions-controls.vue'
import { useRogue } from '@/js/services/Rogue'
import { useMassStorage } from '@/js/composables/useMassStorage'

const three = ref<HTMLDivElement>()

const props = defineProps<{
  controls?: boolean,
  cacheKey?: string, // Reuses engine instance across components (e.g. View in AR)
  engineConfig?: Partial<RogueEngineOptions>,
  buyRequest: BuyRequest, // Set buyRequest OR part/scene, not both
  part?: PartDefinition,
  scene?: {part: PartDefinition, config: RogueEngineOptions}
  lazy?: boolean // Defers engine init until scrolled into view
}>()

const buyRequestRef = toRef(props, 'buyRequest')
const { STORAGE_SKUS, is3DStorageProduct, isMassStorage } = useMassStorage(buyRequestRef)

const rogue = useRogue()
const isCameraDebug = useRoute().query.camera_debug !== undefined
const CameraDebug = defineAsyncComponent(() => import('./ui/camera-debug.vue'))

const areMeasurementsVisible = ref(false)
const isInOverviewMode = ref(false)
const isAnimating = ref(false)
const engineOptions = ref<Partial<RogueEngineOptions>>({})

const productStore = useProductStore()
const products = computed(() => productStore.products)
const groupedProductId = props.buyRequest?.productId
const groupedProduct = computed(() => (groupedProductId ? products.value[groupedProductId] : undefined))
const isTheCave = computed(() => isEnabledCustomProduct(rogue, 'cave', groupedProduct.value))
const isHybridRig = computed(() => isEnabledCustomProduct(rogue, 'hybrid-rig', groupedProduct.value))

// A grouped product with one child renders that child's buy request, so its parent is found through metadata the same way getPart routes it.
const measuredProduct = computed(() => {
  const id = props.buyRequest?.metadata?.groupedProductId || groupedProductId

  return id ? products.value[id] : undefined
})

// Each product decides for itself what gear it can stage - Cave loads a bar onto its jcups, Hybrid has none and stages its own parts instead, the trap bar loads its sleeves.
const supportsEquippedView = computed(() => isTheCave.value || isHybridRig.value || measuredProduct.value?.roguebuilder?.props?.rackbuilder?.type === 'tb3')

const measurementConfig = computed(() =>
  MEASUREMENT_CONFIG.find(e => e.matches(measuredProduct.value))?.config ?? DEFAULT_MEASUREMENT_CONFIG
)

const showDimensions = computed(() => MEASUREMENT_CONFIG.some(e => e.matches(measuredProduct.value)))

const { engine } = use3DEngine({
  container     : three,
  autoInit      : props.lazy !== true,
  key           : props.cacheKey,
  engineOptions : {
    ...engineOptions.value,
    ...(props.engineConfig || props.scene?.config),
  },
}, [])

function setVisible(isVisible: boolean) {
  if (isVisible && props.lazy) {
    engine.init()
  }
}

const showEquipped = computed({
  get : () => !!props.buyRequest?.metadata?.showEquipped,
  set : (val) => {
    if (!props.buyRequest) return
    props.buyRequest.metadata = { ...props.buyRequest.metadata, showEquipped: val }
  },
})

const { builder, loading, updateBuyRequest, cameraFitOffset } = useRackbuilder({
  engine,
  buyRequest : props.buyRequest,
  part       : props.part || props.scene?.part,
})

const imageFlagMedia = computed(() => {
  if (groupedProduct.value?.attributes.patent_pending) {
    return 'patent-pending'
  }

  if (groupedProduct.value?.attributes.is_patented) {
    return 'patented'
  }

  return undefined
})

function resetCamera() {
  engine.engine?.targetCamera('', cameraFitOffset.value ?? 1.35, { updateTarget: true, updateZoom: true })
  engine?.engine?.clearVirtualMeshFocus()
}

// Synced from the engine via onStateChange — drives button state and measurement refresh.
let wasAnimating = false
let wasInOverviewMode = false
const updateOverviewState = () => {
  if (!engine.engine) return

  const nowInOverview = engine.engine.cameraManager.isInOverviewMode
  const nowAnimating = engine.engine.cameraManager.isAnimating

  isInOverviewMode.value = nowInOverview
  isAnimating.value = nowAnimating
  areMeasurementsVisible.value = nowInOverview

  // Overview animation settled — refresh measurements for latest geometry
  if (nowInOverview && wasAnimating && !nowAnimating) {
    engine.engine.measurementOverlayManager.showMeasurements()
  }

  // Exit animation completed — re-fit camera in case geometry changed while overhead
  if (wasInOverviewMode && !nowInOverview) {
    engine.engine.targetCamera('', cameraFitOffset.value ?? measurementConfig.value.defaultZoomLevel, { updateTarget: true, updateZoom: true })
  }

  wasAnimating = nowAnimating
  wasInOverviewMode = nowInOverview

  const config = measurementConfig.value
  const manager = engine.engine.measurementOverlayManager

  manager.setDynamicMeasurements(config.dynamic)

  if (!config.dynamic) {
    const overrides = config.getDimensionOverrides?.(props.buyRequest, products.value) ?? config.dimensionOverrides ?? null
    manager.setDimensionOverrides(overrides)
  }

  if (config.dynamic) {
    if (config.measurementDistance != null) manager.setMeasurementDistance(config.measurementDistance)
    if (config.maxMeasurementHeight != null) manager.setMaxMeasurementHeight(config.maxMeasurementHeight)
    if (config.edgeDistanceThreshold != null) manager.setEdgeDistanceThreshold(config.edgeDistanceThreshold)
    manager.setMeshValueMappings(config.meshValueMappings)
    manager.setGeometryMeshPatterns(config.geometryMeshPatterns)

    if (config.cornerFilterAngles !== undefined) manager.setCornerFilterAngles(config.cornerFilterAngles)
    manager.setInchesPerUnit(config.inchesPerUnit)
  }

  manager.setDynamicOffset(true, config.dynamicOffset ?? 0.08)
  manager.setDynamicThickness(true, config.dynamicThickness ?? 0.001)
  manager.setVerticalPosition(config.verticalPosition ?? 0.05)
  // 0.5 is the engine's own default; there is no setter, so the public field is assigned directly.
  manager.verticalOffset = config.verticalOffset ?? 0.5
  manager.setDynamicCapLength(true, config.dynamicCapLength ?? 0.03)
}

// Also registered in onInit for lazy engines where engine.engine is null at setup
engine.engine?.cameraManager?.onStateChange(updateOverviewState)

const focusedVirtualMeshId = ref<string | null>(null)

const updateVirtualMeshFocusState = (meshId: string | null) => {
  focusedVirtualMeshId.value = meshId
}

engine.onInit(() => {
  engine.engine?.cameraManager?.onStateChange(updateOverviewState)
  updateOverviewState()
  engine.engine?.onVirtualMeshFocusChange(updateVirtualMeshFocusState)
})

const overviewCameraView = () => {
  if (!engine.engine) return

  engine.engine.cameraManager.showOverview(4.5, {
    updateTarget : true,
    updateZoom   : true,
    skipAnimate  : false,
  })
}

const exitOverviewMode = () => {
  engine.engine?.measurementOverlayManager.hideMeasurements()
  if (!engine.engine) return
  engine.engine.cameraManager.exitOverviewMode()
  engine.engine?.clearVirtualMeshFocus()
}

const toggleMeasurements = () => {
  overviewCameraView()
  areMeasurementsVisible.value = true

  engine.engine?.measurementOverlayManager.showMeasurements()
  engine.engine?.measurementOverlayManager.setDirectionalVisibility({
    north : true,
    south : true,
    east  : true,
    west  : true,
  })

  const config = measurementConfig.value
  const zoomLevel = config.getZoomLevel?.(props.buyRequest, products.value) ?? config.defaultZoomLevel

  engine.engine?.cameraManager.updateOverview(zoomLevel, {
    updateTarget : true,
    updateZoom   : true,
    skipAnimate  : false,
  })
}

watch(() => props.buyRequest, (newVal) => {
  updateBuyRequest(newVal || null)
}, { deep: true, immediate: true })
</script>
<style lang="scss" scoped>
.container {
  position: relative;
  width: 100%;
  height: 100%;

  @include not-desktop(){
    max-height: 60vh;
    min-height: 400px;
  }

  &.custom-controls {
    @include mobile() {
      display: flex;
      flex-direction: column;
      max-height: none;
    }
  }
}

.rackbuild {
  position: relative;
  width: 100%;
  height: 100%;

  @include not-desktop(){
    max-height: 60vh;
    min-height: 400px;
  }

  .custom-controls & {
    @include mobile() {
      flex: 1;
      min-height: 0;
      max-height: none;
    }
  }

  .patent-image {
    position: absolute;
    right: 0;
    top: 0;
  }

}

.threecontainer {
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  position: absolute;

  &:not(.loading){
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }
}

.rackbuilder-pad {
  padding-top: 80%;
  pointer-events: none;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.controls {
  position: absolute;

  &.hide-on-mobile {
    @include mobile() {
      display: none;
    }
  }

  @include mobile(){
    bottom: -1px;
    left: 0;
    right: 0;
    padding: 0 $space-s;
  }

  @include not-mobile(){
    bottom: $space-s;
    right: $space-s;
    width: 275px;
  }

  @media screen and (min-width: 1024px) and (max-width: 1100px) {
    width: 150px;
  }
}

.threed-icon {
  width: 32px;
  height: 32px;
  position: absolute;
  top: $space-s;
  right: $space-s;
  margin-top: $size-xxs;

  @include not-mobile(){
    width: 64px;
    height: 64px;
  }
}
</style>
